<template>
  <div class="provider-nca-keys">
    <div class="header">
      <h4>ЭЦП-ключи поставщиков</h4>
      <div class="header__actions">
        <CustomButton
          label="Автоподписание ТД"
          icon="draw"
          color="primary"
          :loading="autosignLoading"
          @click="openAutosignModal"
        />
        <CustomButton
          label="Загрузить ключ"
          icon="add"
          @click="openUploadModal"
        />
      </div>
    </div>

    <CustomTable
      :columns="columns"
      :rowsData="keys"
      :loading="loading"
      :row-key="'provider_nca_key_store_id'"
      :custom-body="true"
    >
      <template v-slot:customBody="props">
        <q-tr :props="props">
          <q-td>
            <div class="key-name">
              <q-icon name="vpn_key" size="18px" :color="props.row.is_active ? 'primary' : 'grey'" />
              <span>{{ props.row.key_alias }}</span>
            </div>
          </q-td>
          <q-td>
            {{ props.row.provider_name }}
          </q-td>
          <q-td class="text-center">
            <q-badge
              :color="props.row.is_active ? 'positive' : 'grey'"
              :label="props.row.is_active ? 'Активен' : 'Неактивен'"
            />
          </q-td>
          <q-td>
            {{ formatDate(props.row.created_at) }}
          </q-td>
          <q-td class="text-center">
            <div class="actions">
              <q-btn
                v-if="!props.row.is_active"
                flat
                dense
                round
                icon="check_circle"
                color="positive"
                size="sm"
                :loading="actionLoading[props.row.provider_nca_key_store_id]"
                @click="activateKey(props.row.provider_nca_key_store_id)"
              >
                <q-tooltip>Активировать</q-tooltip>
              </q-btn>
              <q-btn
                v-if="props.row.is_active"
                flat
                dense
                round
                icon="block"
                color="warning"
                size="sm"
                :loading="actionLoading[props.row.provider_nca_key_store_id]"
                @click="deactivateKey(props.row.provider_nca_key_store_id)"
              >
                <q-tooltip>Деактивировать</q-tooltip>
              </q-btn>
              <q-btn
                flat
                dense
                round
                icon="delete"
                color="negative"
                size="sm"
                :loading="actionLoading[props.row.provider_nca_key_store_id]"
                @click="confirmDelete(props.row.provider_nca_key_store_id)"
              >
                <q-tooltip>Удалить</q-tooltip>
              </q-btn>
            </div>
          </q-td>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import CustomButton from "@/components/common/CustomButton";
import CustomTable from "@/components/common/CustomTable";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { providerNcaKeyStoreAPI } from "@/services/api/providerNcaKeyStoreApi";
import { ncaAutosignAPI } from "@/services/api/ncaAutosignApi";
import { MODAL_NAMES } from "@/constants";

export default {
  name: "ProviderNcaKeys",
  components: { CustomButton, CustomTable },
  data() {
    return {
      loading: false,
      keys: [],
      actionLoading: {},
      autosignLoading: false,
      columns: [
        {
          name: "key_alias",
          label: "Название ключа",
          field: "key_alias",
          align: "left",
          sortable: true,
        },
        {
          name: "provider_name",
          label: "Поставщик",
          field: "provider_name",
          align: "left",
          sortable: true,
        },
        {
          name: "is_active",
          label: "Статус",
          field: "is_active",
          align: "center",
          sortable: true,
        },
        {
          name: "created_at",
          label: "Дата загрузки",
          field: "created_at",
          align: "left",
          sortable: true,
        },
        {
          name: "actions",
          label: "Действия",
          field: "actions",
          align: "center",
        },
      ],
    };
  },
  methods: {
    async loadKeys() {
      this.loading = true;
      try {
        const res = await providerNcaKeyStoreAPI.readAll();
        this.keys = res?.data || [];
      } catch (error) {
        this.showErrorSnackbar("Ошибка при загрузке списка ключей");
      } finally {
        this.loading = false;
      }
    },
    openUploadModal() {
      this.showModal(MODAL_NAMES.UploadProviderNcaKey, {
        title: "Загрузить ЭЦП-ключ",
        onSubmit: async () => {
          await this.loadKeys();
        },
      });
    },
    async openAutosignModal() {
      // Проверяем есть ли активные ключи
      const activeKeys = this.keys.filter(k => k.is_active);
      if (activeKeys.length === 0) {
        this.showErrorSnackbar("Нет активных ключей для автоподписания");
        return;
      }
      
      try {
        // Получаем список накладных
        const res = await ncaAutosignAPI.getPendingInvoices();
        const pendingCount = res?.count || 0;
        
        if (pendingCount === 0) {
          this.showErrorSnackbar("Нет накладных для автоподписания");
          return;
        }
        
        // Показываем модалку подтверждения
        this.showModal(MODAL_NAMES.CONFIRM, {
          title: `Автоподписание накладных ТД`,
          description: `Найдено ${pendingCount} накладных для автоподписания. Выберите ключ для подписания.`,
          customContent: true,
          onSubmit: async (keyId) => {
            await this.runAutosign(keyId || activeKeys[0].provider_nca_key_store_id);
          },
        });
      } catch (error) {
        this.showErrorSnackbar("Ошибка при получении списка накладных");
      }
    },
    async runAutosign(keyId) {
      this.autosignLoading = true;
      try {
        const res = await ncaAutosignAPI.runAutosign(keyId);
        const results = res?.data;
        
        if (results) {
          const message = `Подписано: ${results.success}/${results.total}. Ошибок: ${results.failed}`;
          if (results.failed > 0) {
            this.showErrorSnackbar(message);
          } else {
            this.showSucceedSnackbar(message);
          }
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Ошибка при автоподписании";
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.autosignLoading = false;
      }
    },
    async activateKey(keyId) {
      this.actionLoading[keyId] = true;
      try {
        await providerNcaKeyStoreAPI.activate(keyId);
        this.showSucceedSnackbar("Ключ активирован");
        await this.loadKeys();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Ошибка при активации ключа";
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.actionLoading[keyId] = false;
      }
    },
    async deactivateKey(keyId) {
      this.actionLoading[keyId] = true;
      try {
        await providerNcaKeyStoreAPI.deactivate(keyId);
        this.showSucceedSnackbar("Ключ деактивирован");
        await this.loadKeys();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Ошибка при деактивации ключа";
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.actionLoading[keyId] = false;
      }
    },
    confirmDelete(keyId) {
      this.showModal(MODAL_NAMES.CONFIRM, {
        title: "Удалить ключ?",
        description: "Это действие нельзя отменить. Все зашифрованные данные будут удалены.",
        onSubmit: async () => {
          await this.deleteKey(keyId);
        },
      });
    },
    async deleteKey(keyId) {
      this.actionLoading[keyId] = true;
      try {
        await providerNcaKeyStoreAPI.delete(keyId);
        this.showSucceedSnackbar("Ключ удален");
        await this.loadKeys();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Ошибка при удалении ключа";
        this.showErrorSnackbar(errorMessage);
      } finally {
        this.actionLoading[keyId] = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  mounted() {
    this.loadKeys();
  },
  setup() {
    const { showModal } = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showModal, showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style scoped lang="scss">
.provider-nca-keys {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h4 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    gap: 12px;
  }
}

.key-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.actions {
  display: flex;
  gap: 4px;
  justify-content: center;
}

:deep(.header__actions button) {
  width: auto !important;
  white-space: nowrap;
}
</style>
